import { useState } from 'react';
import { Button, Input, SelectField, SettingsSection } from '@/design-system';
import { Copy, Eye, EyeOff, Info, Save, Settings2 } from 'lucide-react';
import type { MetaSettingsData } from '../../api/meta-settings.service';
import { useMetaSettings } from '../../hooks/useMetaSettings';

function SecretField({ label, hint, value, onChange }: {
  label: string; hint: string; value: string; onChange: (value: string) => void;
}) {
  const [show, setShow] = useState(false);
  return <div>
    <div className="relative">
      <Input label={label} type={show && value !== '••••••••••' ? 'text' : 'password'} value={value}
        onChange={(event) => onChange(event.target.value)} className="pr-10" />
      <Button variant="ghost" size="sm" className="absolute bottom-0.5 right-1 px-1"
        aria-label={show ? `Ocultar ${label}` : `Mostrar ${label}`} onClick={() => setShow(!show)}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </Button>
    </div>
    <p className="mt-1 text-xs text-gray-500">{value === '••••••••••' ? 'Configurado. Informe um novo valor para substituir a chave. ' : ''}{hint}</p>
  </div>;
}

function UrlField({ label, value, hint }: { label: string; value?: string | null; hint: string }) {
  const [feedback, setFeedback] = useState('');
  return <div>
    <div className="relative">
      <Input label={label} value={value ?? ''} readOnly placeholder="Não configurado no ambiente" className="pr-10" />
      <Button variant="ghost" size="sm" disabled={!value} className="absolute bottom-0.5 right-1 px-1"
        aria-label={`Copiar ${label}`} onClick={() => {
          if (value) void navigator.clipboard.writeText(value).then(() => setFeedback('Copiado.')).catch(() => setFeedback('Não foi possível copiar.'));
        }}><Copy size={16} /></Button>
    </div>
    <p className="mt-1 text-xs text-gray-500">{hint}</p>
    {feedback && <p role="status" className="mt-1 text-xs text-gray-500">{feedback}</p>}
  </div>;
}

export function MetaSection() {
  const { query, save } = useMetaSettings();
  const [draft, setDraft] = useState<Partial<MetaSettingsData>>({});
  const [customVersion, setCustomVersion] = useState(false);
  if (query.isPending) return <p role="status" className="text-sm text-gray-500">Carregando credenciais Meta…</p>;
  if (!query.data) return <div role="alert" className="space-y-3 text-sm text-red-700">
    Não foi possível carregar as credenciais Meta.
    <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button>
  </div>;
  const data = { ...query.data, ...draft };
  const patch = (partial: Partial<MetaSettingsData>) => { setDraft((previous) => ({ ...previous, ...partial })); save.reset(); };
  const version = data.metaGraphVersion || 'v25.0';
  return <div className="space-y-5">
    {save.isError && <p role="alert" className="text-sm text-red-700">Não foi possível salvar as credenciais Meta. Tente novamente.</p>}
    {save.isSuccess && <p role="status" className="text-sm text-emerald-700">Credenciais salvas.</p>}
    <fieldset disabled={save.isPending} className="min-w-0">
      <SettingsSection title="App Meta principal" icon={Settings2}
        description="Um único app para WhatsApp Cloud, Instagram conectado pelo Facebook e Messenger.">
        <div className="space-y-5 pt-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div><Input label="App ID" value={data.metaAppId ?? ''} onChange={(event) => patch({ metaAppId: event.target.value })} />
              <p className="mt-1 text-xs text-gray-500">ID do App Meta principal usado pelo WhatsApp Cloud, Instagram via Facebook e Messenger.</p></div>
            <SecretField label="App Secret" hint="Credencial secreta do aplicativo Meta." value={data.metaAppSecret ?? ''} onChange={(value) => patch({ metaAppSecret: value })} />
            <div><Input label="Config ID" value={data.metaConfigId ?? ''} onChange={(event) => patch({ metaConfigId: event.target.value })} />
              <p className="mt-1 text-xs text-gray-500">Usado no fluxo de Embedded Signup da Meta.</p></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <UrlField label="Redirect URI (Facebook Login)" value={data.metaInboxRedirectUri} hint="Use em Facebook Login > Configurações para Instagram via Facebook e Messenger. Definido no ambiente ou derivado do callback WhatsApp." />
            <div className="space-y-2">
              <SelectField label="Versão da Graph API" value={customVersion ? 'custom' : version}
                onChange={(value) => { setCustomVersion(value === 'custom'); if (value !== 'custom') patch({ metaGraphVersion: value }); }}
                options={[...Array.from(new Set([version, 'v25.0'])).map((value) => ({ value, label: value })), { value: 'custom', label: 'Outra versão' }]}
                hint="Use a versão configurada para o app no painel da Meta." />
              {customVersion && <Input label="Versão personalizada" placeholder="vXX.0" value={data.metaGraphVersion ?? ''}
                onChange={(event) => patch({ metaGraphVersion: event.target.value })} />}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <UrlField label="URL Webhook (WhatsApp)" value={data.metaWebhookUrl} hint="Use na configuração de Webhooks do WhatsApp." />
            <UrlField label="URL Webhook (Instagram e Messenger)" value={data.metaWebhookUrl} hint="Use na configuração de Webhooks do Instagram via Facebook e Messenger." />
            <SecretField label="Token de verificação do webhook" value={data.metaWebhookVerifyToken ?? ''}
              onChange={(value) => patch({ metaWebhookVerifyToken: value })} hint="Use o mesmo valor configurado como META_WEBHOOK_VERIFY_TOKEN no webhook buffer." />
          </div>
          <p className="text-xs text-gray-500">Crie ou gerencie este app em <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-primary underline">developers.facebook.com/apps</a>.</p>
          <details className="border-t border-gray-100 pt-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">Configurações avançadas</summary>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div><Input type="url" label="Redirect URI (WhatsApp Cloud)" value={data.metaRedirectUri ?? ''} onChange={(event) => patch({ metaRedirectUri: event.target.value })} />
                <p className="mt-1 text-xs text-gray-500">Callback OAuth do WhatsApp Cloud. Deve coincidir com o aplicativo Meta.</p></div>
              <SecretField label="Token de acesso global" value={data.metaAccessToken ?? ''} onChange={(value) => patch({ metaAccessToken: value })}
                hint="Tem prioridade sobre os tokens OAuth dos canais. Deixe vazio para usar os tokens individuais por empresa." />
            </div>
          </details>
        </div>
      </SettingsSection>
    </fieldset>
    <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
      <Info size={16} className="mt-0.5 shrink-0" />Configurações de credenciais aplicam após salvar (cache de 60s). Sem necessidade de reiniciar o servidor.
    </div>
    <div className="flex justify-end"><Button isLoading={save.isPending} disabled={!Object.keys(draft).length}
      onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}><Save size={16} />Salvar credenciais</Button></div>
  </div>;
}
